import { useEffect, useState } from "react";

const AGENT_ID = process.env.REACT_APP_ELEVENLABS_AGENT_ID;

// Norwegian UI labels for the widget (official `text_contents` keys). Only UI text is changed;
// agent prompt, voice, first message and tools stay exactly as configured in ElevenLabs.
const TEXT_NO = {
  main_label: "Trenger du hjelp?", start_call: "Start en samtale", start_chat: "Start en chat",
  new_call: "Ny samtale", end_call: "Avslutt samtale", mute_microphone: "Slå av mikrofon",
  change_language: "Bytt språk", collapse: "Lukk", expand: "Åpne", copied: "Kopiert!",
  accept_terms: "Godta", dismiss_terms: "Avbryt", listening_status: "Lytter…",
  speaking_status: "Vadim snakker", connecting_status: "Kobler til…", chatting_status: "Chatter",
  queue_waiting_status: "Venter på ledig linje…", queue_waiting_status_short: "Venter…",
  input_label: "Melding", input_placeholder: "Skriv en melding…",
  input_placeholder_text_only: "Skriv en melding…", input_placeholder_new_conversation: "Start en ny samtale…",
  user_ended_conversation: "Du avsluttet samtalen", agent_ended_conversation: "Samtalen er avsluttet",
  conversation_id: "Samtale-ID", error_occurred: "Det oppstod en feil. Prøv igjen.",
  queue_timed_out: "Ingen ledig linje akkurat nå. Prøv igjen senere.", copy_id: "Kopier ID",
  initiate_feedback: "Hvordan var samtalen?", request_follow_up_feedback: "Fortell oss gjerne mer",
  thanks_for_feedback: "Takk for tilbakemeldingen!", thanks_for_feedback_details: "Den hjelper oss å bli bedre.",
  follow_up_feedback_placeholder: "Skriv tilbakemelding…", submit: "Send", go_back: "Tilbake",
  send_message: "Send melding", text_mode: "Tekst", voice_mode: "Tale",
  switched_to_text_mode: "Byttet til tekst", switched_to_voice_mode: "Byttet til tale",
  copy: "Kopier", download: "Last ned", wrap: "Bryt linjer", agent_working: "Jobber…",
  agent_done: "Ferdig", agent_error: "Noe gikk galt", attach_file: "Legg ved fil", remove_file: "Fjern fil",
  file_upload_error: "Opplasting feilet", file_type_unsupported: "Filtypen støttes ikke",
  file_too_large: "Filen er for stor", file_limit_reached: "Maks antall filer nådd",
  typing_indicator: "Skriver…", rich_content_unavailable: "Innholdet kan ikke vises",
};

// Loads the ElevenLabs widget. The live dashboard config is fetched and only its UI
// texts are replaced with Norwegian via the widget's `override-config` attribute.
export default function ElevenLabsWidget({ autoStart = false }) {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!AGENT_ID) return;
    fetch(`https://api.elevenlabs.io/v1/convai/agents/${AGENT_ID}/widget`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      // Text input off: only the BKT-connected chat handles text. Agent/voice/prompt untouched.
      .then((d) => setConfig(JSON.stringify({ ...d.widget_config, text_input_enabled: false,
        text_contents: { ...(d.widget_config.text_contents || {}), ...TEXT_NO } })))
      .catch(() => setConfig(""));
    if (document.getElementById("elevenlabs-convai-script")) return;
    const s = document.createElement("script");
    s.id = "elevenlabs-convai-script";
    s.src = "https://unpkg.com/@elevenlabs/convai-widget-embed";
    s.async = true;
    s.type = "text/javascript";
    document.body.appendChild(s);
  }, []);

  useEffect(() => {
    if (!autoStart || config === null) return;
    let tries = 0;
    const iv = setInterval(() => { if (openAssistant() || ++tries > 40) clearInterval(iv); }, 250);
    return () => clearInterval(iv);
  }, [autoStart, config]);

  if (!AGENT_ID || config === null) return null;
  return config ? (
    <elevenlabs-convai agent-id={AGENT_ID} override-config={config}></elevenlabs-convai>
  ) : (
    <elevenlabs-convai agent-id={AGENT_ID}></elevenlabs-convai>
  );
}

// Starts the voice call by pressing the widget's own "Start en samtale" button.
export function openAssistant() {
  const el = document.querySelector("elevenlabs-convai");
  if (!el) return false;
  el.scrollIntoView?.({ behavior: "smooth", block: "end" });
  const btn = [...(el.shadowRoot?.querySelectorAll("button") || [])].find((b) => b.textContent.includes(TEXT_NO.start_call));
  if (btn) { btn.click(); return true; }
  return false;
}
